import { Form, Input, Modal, Typography } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { REVENUE_KEYS } from 'constants/analytics';
import { LANGUAGES } from 'constants/languages';
import {
  createOrganization,
  fetchOrganizationById,
  updateOrganization
} from 'services/api/organizations';
import { OrganizationInput } from 'types/api/organizations';

import { showErrorMessage } from 'utils/showErrorMessage';

const validatePercent = (value: string) => {
  return /^100%?$|^(\d{1,2})(\.\d+)?%?$/.test(value);
};

const { TextArea } = Input;

interface IProps {
  refetch(): void;
  onClose(): void;
  id?: string;
}

const OrganizationsModal: FC<IProps> = ({ id, refetch, onClose }) => {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const result = await fetchOrganizationById(id);
        if (cancelled || !result) return;
        // BE's Organization entity emits camelCase (additionalInfo,
        // parentId, sortIndex, bgImage, ...) even though the DB
        // columns and the write DTO are snake_case. Read from the
        // camelCase source; fall back to snake_case for safety in
        // case BE ever swaps the response shape.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const raw = result as Record<string, any>;
        form.setFieldsValue({
          name: raw.name || {},
          additional_info: raw.additionalInfo ?? raw.additional_info ?? '',
          commission: raw.commission || {}
        });
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const onFinish = useCallback(
    async (values: OrganizationInput) => {
      try {
        setIsSaving(true);

        if (id) {
          await updateOrganization(id, values);
        } else {
          await createOrganization(values);
        }

        refetch();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, refetch]
  );

  return (
    <Modal
      title={id ? 'Edit' : 'Create'}
      onCancel={closeModal}
      open
      width={600}
      onOk={form.submit}
      confirmLoading={isSaving}
      destroyOnClose
    >
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        {LANGUAGES.map(item => (
          <Form.Item
            key={item.locale}
            name={['name', item.locale]}
            label={`Name ${item.label}`}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        ))}
        <Form.Item
          name="additional_info"
          label="Additional information"
          rules={[{ required: false }]}
        >
          <TextArea />
        </Form.Item>
        <Typography.Title level={4}>Commissions</Typography.Title>
        {REVENUE_KEYS.map(key => (
          <Form.Item
            key={key}
            name={['commission', key]}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || validatePercent(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Enter a valid percentage (e.g., 50%)')
                  );
                }
              }
            ]}
            label={key}
          >
            <Input prefix="%" />
          </Form.Item>
        ))}
      </Form>
    </Modal>
  );
};

export default memo(OrganizationsModal);
